/**
 * TimeEntryHistory Component
 *
 * Displays time entry history grouped by day with totals.
 * Supports daily and weekly views.
 *
 * @module pages/technician/time/TimeEntryHistory
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, Badge, Accordion } from 'react-bootstrap';
import TimeEntryCard from './TimeEntryCard';
import './TimeEntryPage.css';

/**
 * Group entries by date
 * @param {Array} entries - Array of time entries
 * @returns {Object} Entries grouped by date string
 */
const groupByDate = (entries) => {
  const groups = {};

  entries.forEach((entry) => {
    const date = new Date(entry.check_in_time);
    const dateKey = date.toISOString().split('T')[0];

    if (!groups[dateKey]) {
      groups[dateKey] = {
        date: dateKey,
        entries: [],
        totalHours: 0,
      };
    }

    groups[dateKey].entries.push(entry);
    groups[dateKey].totalHours += entry.total_hours || 0;
  });

  // Sort by date descending
  return Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));
};

/**
 * Format date for display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Formatted date
 */
const formatDate = (dateString) => {
  const date = new Date(dateString + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);

  if (dateOnly.getTime() === today.getTime()) {
    return 'Hoy';
  } else if (dateOnly.getTime() === yesterday.getTime()) {
    return 'Ayer';
  }

  return date.toLocaleDateString('es-CR', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });
};

/**
 * Format hours for display
 * @param {number} hours - Total hours (decimal)
 * @returns {string} Formatted hours
 */
const formatHours = (hours) => {
  if (!hours || hours === 0) return '0h';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/**
 * Get week number from date
 * @param {Date} date - Date object
 * @returns {number} Week number
 */
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const TimeEntryHistory = ({
  entries = [],
  title = 'Historial',
  showWeekSummary = true,
  onEntryClick,
  emptyMessage = 'No hay registros',
}) => {
  const groupedEntries = useMemo(() => groupByDate(entries), [entries]);

  const weekTotals = useMemo(() => {
    const totals = {};

    entries.forEach((entry) => {
      const date = new Date(entry.check_in_time);
      const weekKey = `${date.getFullYear()}-W${getWeekNumber(date)}`;

      if (!totals[weekKey]) {
        totals[weekKey] = { hours: 0, entries: 0 };
      }

      totals[weekKey].hours += entry.total_hours || 0;
      totals[weekKey].entries += 1;
    });

    return totals;
  }, [entries]);

  // Calculate current week totals
  const currentWeekTotal = useMemo(() => {
    const now = new Date();
    const currentWeekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
    return weekTotals[currentWeekKey] || { hours: 0, entries: 0 };
  }, [weekTotals]);

  if (entries.length === 0) {
    return (
      <Card className="time-entry-history empty">
        <Card.Body className="text-center py-4">
          <i className="bi bi-clock-history display-4 text-muted mb-3"></i>
          <p className="text-muted mb-0">{emptyMessage}</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="time-entry-history">
      {/* Week summary */}
      {showWeekSummary && (
        <Card className="week-summary-card mb-3">
          <Card.Body>
            <div className="week-summary">
              <div className="week-summary-item">
                <span className="week-summary-value">{formatHours(currentWeekTotal.hours)}</span>
                <span className="week-summary-label">Esta semana</span>
              </div>
              <div className="week-summary-divider"></div>
              <div className="week-summary-item">
                <span className="week-summary-value">{currentWeekTotal.entries}</span>
                <span className="week-summary-label">Registros</span>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Daily groups */}
      <Accordion defaultActiveKey="0" className="history-accordion">
        {groupedEntries.map((group, index) => (
          <Accordion.Item eventKey={index.toString()} key={group.date}>
            <Accordion.Header>
              <div className="history-day-header">
                <span className="history-day-date">{formatDate(group.date)}</span>
                <Badge bg="primary" className="history-day-hours">
                  {formatHours(group.totalHours)}
                </Badge>
              </div>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="history-entries">
                {group.entries.map((entry) => (
                  <TimeEntryCard
                    key={entry.id}
                    entry={entry}
                    onClick={onEntryClick ? () => onEntryClick(entry) : undefined}
                  />
                ))}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

TimeEntryHistory.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      check_in_time: PropTypes.string.isRequired,
      total_hours: PropTypes.number,
    })
  ),
  title: PropTypes.string,
  showWeekSummary: PropTypes.bool,
  onEntryClick: PropTypes.func,
  emptyMessage: PropTypes.string,
};

export default TimeEntryHistory;
